import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';

const TermsOfService = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b px-4 md:px-6 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center h-16">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/appsytec-logo.png"
              alt="Appsytec Logo"
              className="h-10 w-auto object-contain"
            />
          </Link>
          <nav className="ml-auto flex gap-6">
            <Link to="/" className="text-sm font-medium hover:underline hover:text-primary">
              Home
            </Link>
            <Link to="/login" className="text-sm font-medium hover:underline hover:text-primary">
              Login
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full p-4 md:p-8">
        <Card className="mt-8">
          <CardContent className="prose prose-sm max-w-none p-6 md:p-8">
            <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>

            <p className="text-sm text-muted-foreground mb-6">Last updated: January 2025</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing or using the Appsytec Pty Ltd website, platform, or services ("Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you must not use our Services.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Services</h2>
            <p className="mb-4">
              Appsytec Pty Ltd provides recruitment, consulting, and related technology services, including candidate profiling, job matching, and client engagement tools. We may modify or discontinue services at any time without notice.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. User Accounts</h2>
            <p className="mb-4">
              You may be required to create an account to access certain features. You are responsible for:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>Ensuring information you provide is accurate and current</li>
              <li>All activity that occurs under your account</li>
            </ul>
            <p className="mb-4">
              We reserve the right to suspend or terminate accounts that breach these Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Use of Information</h2>
            <p className="mb-4">
              Your use of the Services is subject to our Privacy Policy, which explains how we collect, use, and protect your personal information.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. User Obligations</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide false or misleading information</li>
              <li>Upload content you do not have the right to share</li>
              <li>Use the Services for unlawful, harmful, or deceptive purposes</li>
              <li>Interfere with the security or functionality of the platform</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Intellectual Property</h2>
            <p className="mb-4">
              All content, software, branding, and materials provided through the Services are owned by or licensed to Appsytec Pty Ltd. You may not copy, modify, distribute, or reverse engineer any part of the Services without written permission.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Third-Party Services</h2>
            <p className="mb-4">
              Our Services may integrate with or link to third-party platforms. We are not responsible for third-party services, content, or practices.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Disclaimer</h2>
            <p className="mb-4">
              The Services are provided "as is" and "as available." We make no warranties regarding accuracy, reliability, or availability of the Services or employment outcomes.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="mb-4">
              To the maximum extent permitted by law, Appsytec Pty Ltd will not be liable for indirect, incidental, or consequential damages arising from your use of the Services.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Termination</h2>
            <p className="mb-4">
              We may suspend or terminate your access to the Services at our discretion, including for breach of these Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">11. Governing Law</h2>
            <p className="mb-4">
              These Terms are governed by the laws of Victoria, Australia. Any disputes will be subject to the exclusive jurisdiction of Victorian courts.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">12. Changes to Terms</h2>
            <p className="mb-4">
              We may update these Terms from time to time. Continued use of the Services constitutes acceptance of the updated Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">13. Contact</h2>
            <p className="mb-2">
              <strong>Appsytec Pty Ltd</strong>
            </p>
            <p className="mb-1">
              Email: <a href="mailto:contact@appsytec.com" className="text-primary hover:underline">contact@appsytec.com</a>
            </p>
            <p className="mb-4">
              Location: Melbourne, Australia
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Appsytec. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/terms-of-service" className="text-sm text-muted-foreground hover:text-foreground">
                Terms of Service
              </Link>
              <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-sm text-muted-foreground hover:text-foreground">
                Cookie Policy
              </Link>
              <Link to="/modern-slavery-statement" className="text-sm text-muted-foreground hover:text-foreground">
                Modern Slavery Statement
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
